import React, { useRef, useState, useMemo } from 'react';
import { Modal, message, Spin, Button } from 'antd';
import { CommonForm, FileUpload } from '@jd/x-coreui';
import { repairApi } from '@/fetch/bussiness/repair';
import { formConfig } from './constant';
import './index.scss';
import { HttpStatusCode } from '@/fetch/core/constant';
import { videoExtensions, imageExtensions } from '@/utils/index';
import { ValidationMap, ValidationCtx } from './constant';

interface RepairOrderAddModalProps {
  visible?: boolean;
  vehicleName: string;
  issueNumber?: string;
  onCancel: () => void;
}

const RepairOrderAddModal: React.FC<RepairOrderAddModalProps> = ({
  visible = true,
  vehicleName,
  issueNumber,
  onCancel,
}) => {
  const formRef = useRef<any>(); // 表单实例
  const [uploading, setUploading] = useState(false); // 文件的上传状态
  const [fileList, setFileList] = useState<any[]>([]); // 已上传的文件列表
  const [loading, setLoading] = useState<boolean>(false); // 提交维修单的loading态

  /**
   * 提交前校验维修单字段
   * @return {*}
   */
  const handleValidateBeforeSubmit = () => {
    // 构造校验上下文
    const ctx: ValidationCtx = {
      formRef,
      vehicleName,
      uploading,
      fileList,
    };
    // 校验
    const error = ValidationMap.find((item) => item.when(ctx));
    if (!error) return true;
    // 校验不通过提示错误信息
    message.error(error.message || '请完善表单信息！');
    return false;
  };

  /**
   * 提交维修单
   * @return {*}
   */
  const handleSubmit = async () => {
    const isValidate = handleValidateBeforeSubmit();
    if (!isValidate) return;
    try {
      setLoading(true);
      const values = await formRef.current?.validateFields();
      // 构造媒体文件列表
      const pictureList: string[] = [];
      let video = null;
      fileList.forEach((fileKey: any) => {
        const isVideo = videoExtensions.some((ext) =>
          fileKey?.toLowerCase()?.endsWith(ext)
        );
        if (isVideo) {
          fileKey && (video = fileKey);
        } else {
          fileKey && pictureList.push(fileKey);
        }
      });
      // 构造入参
      const requestData = {
        vehicleName,
        description: values.description,
        pictureList: pictureList?.length > 0 ? pictureList : null,
        video,
        isInfluenceOperation: values.isInfluenceOperation,
        issueNumber: issueNumber || null,
        appointmentRepairTime: values.appointmentRepairTime
          ? values.appointmentRepairTime?.format('YYYY-MM-DD HH:mm:ss')
          : null,
      };
      // 调用接口创建维修单
      const res = await repairApi.addVehicleRequire(requestData);
      if (res && res.code === HttpStatusCode.Success) {
        message.success('维修单创建成功');
        handleCancel();
      } else {
        throw new Error(res?.message || '创建失败');
      }
    } catch (error: any) {
      message.error(error?.message || '请检查表单信息');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 关闭弹窗前的回调
   */
  const handleCancel = () => {
    onCancel && onCancel();
  };

  /**
   * 文件上传前的校验
   * @param {string} fileName
   * @return {*}
   */
  const handleFileValidate = (fileName: string) => {
    const isVideo = videoExtensions.some((ext) =>
      fileName.toLowerCase().endsWith(ext)
    );
    const isImage = imageExtensions.some((ext) =>
      fileName.toLowerCase().endsWith(ext)
    );
    if (isVideo) {
      const videoCount = fileList.filter((file: any) =>
        videoExtensions.some((ext) => file?.toLowerCase().endsWith(ext))
      ).length;
      if (videoCount >= 1) {
        return {
          isValid: false,
          message: '最多只能上传1个视频',
        };
      }
    } else if (isImage) {
      const imageCount = fileList.filter((file: any) =>
        imageExtensions.some((ext) => file?.toLowerCase().endsWith(ext))
      ).length;
      if (imageCount >= 3) {
        return {
          isValid: false,
          message: '最多只能上传3张照片',
        };
      }
    }
    return true;
  };

  /**
   * 文件上传成功后的回调
   * @param {string} fileKey
   */
  const onFileUploading = (fileKey: string) => {
    const newFileList = [...fileList, fileKey];
    setFileList(newFileList);
  };

  /**
   * 文件删除的回调
   * @param {string} fileKey
   */
  const onFileDelete = (fileKey: string) => {
    const newFileList = fileList.filter((key) => key !== fileKey);
    setFileList(newFileList);
  };

  /**
   * 文件上传失败的回调
   */
  const onFileUploadFailed = () => {
    message.error('文件上传失败');
  };

  /**
   * 渲染弹窗底部按钮
   */
  const modalFooter = useMemo(() => {
    return (
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel}>取消</Button>
        <Button onClick={handleSubmit} loading={loading} type="primary">
          确定
        </Button>
      </div>
    );
  }, [loading, handleSubmit, handleCancel]);

  return (
    <Modal
      title={`车号 ${vehicleName}`}
      open={visible}
      onCancel={handleCancel}
      width={1000}
      destroyOnClose
      footer={modalFooter}
    >
      <div className="repair-order-modal">
        <CommonForm
          formConfig={formConfig}
          getFormInstance={(ref) => (formRef.current = ref)}
          layout="horizontal"
          formType="edit"
          theme="dark"
        />
        <div className="upload-container">
          <div className="media-field">
            <span>*</span>上传图片或视频
          </div>
          <Spin spinning={uploading}>
            <FileUpload
              maxCount={4}
              accept="image/*,video/*"
              fileListType={['picture', 'video']}
              getPreSignatureUrl={`${location.protocol}//${process.env.JDX_APP_CLOUD_FETCH_DOMAIN}/infrastructure/oss/getPreUrl`}
              LOPDN={process.env.JDX_APP_REQUEST_HEADER!}
              bucketName="rover-operation"
              needMd5Validete={false}
              maxFileSize={50}
              unit="MB"
              uploadBtn={{
                type: 'dragger',
                btnText: '添加文件',
                draggerConfig: {
                  title: '点击或拖拽文件到此处上传',
                  hint: '支持1个视频或3张图片',
                },
              }}
              onEnd={onFileUploading}
              onError={onFileUploadFailed}
              onDelete={onFileDelete}
              extensionValidate={handleFileValidate}
              onUploadingChange={setUploading}
            />
          </Spin>
        </div>
      </div>
    </Modal>
  );
};

export default RepairOrderAddModal;
